import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { CalendarClock, Loader2 } from "lucide-react";
import { NumberField, useFieldErrors } from "@/components/DecimalInput";
import { INVALID_FORM_MSG, intOpts, parseFields, toFieldText } from "@/lib/decimal";

// Grenzen wie im Backend (zahlungsfrist.py)
const FIELDS = [
  ["frist_tage", "Zahlungsfrist (Tage)", intOpts(1, 90)],
  ["erinnerung_tage_vorher", "Erinnerung vor Ablauf (Tage)", intOpts(0, 30)],
  ["reaktivierung_tage", "Reaktivierung durch Kunden möglich (Tage nach Stornierung)", intOpts(0, 90)],
  ["ueberweisung_rueckfrage_tage", "Rückfrage bei Überweisung nach (Tage)", intOpts(1, 60)],
];
const SPEC = Object.fromEntries(FIELDS.map(([k, , o]) => [k, o]));

export const ZahlungsfristSettings = () => {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState("");
  const [errors, setErrors, clearErr] = useFieldErrors();
  useEffect(() => { api.get("/zahlungsfrist-config").then((r) => setCfg(toFieldText(r.data, SPEC))).catch(() => {}); }, []);
  if (!cfg) return null;
  const save = async () => {
    const p = parseFields(cfg, SPEC);
    if (!p.ok) { setErrors(p.errors); toast.error(INVALID_FORM_MSG); return; }
    setBusy("save");
    try { const r = await api.put("/zahlungsfrist-config", { ...cfg, ...p.values }); setCfg(toFieldText(r.data, SPEC)); toast.success("Zahlungsfrist-Einstellungen gespeichert."); }
    catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Speichern fehlgeschlagen."); }
    finally { setBusy(""); }
  };
  const run = async () => {
    setBusy("run");
    try { const r = await api.post("/reminder-config/run"); toast.success(`Geprüft: ${r.data.erinnerungen} Erinnerung(en), ${r.data.storniert} storniert, ${r.data.rueckfragen} Rückfrage(n).`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehlgeschlagen."); }
    finally { setBusy(""); }
  };
  return (
    <Card className="border-slate-200" data-testid="zahlungsfrist-settings">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Zahlungsfrist &amp; automatische Stornierung</CardTitle>
        <p className="text-sm text-slate-500 mt-1">Frist startet mit dem Versand der Angebots-Mail. Eine Erinnerung vor Ablauf, danach automatische Stornierung mit Reaktivierungs-Link. Wählt der Kunde Überweisung, wird nie automatisch storniert – das Team wird täglich gefragt, ob die Zahlung eingegangen ist.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Switch checked={cfg.auto_storno} onCheckedChange={(v) => setCfg({ ...cfg, auto_storno: v })} data-testid="zf-auto-storno-switch" />
          <span className="text-sm">Automatische Stornierung unbezahlter Angebote</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {FIELDS.map(([k, l, o]) => (
            <div key={k}><Label htmlFor={`zf-${k}`} className="text-xs">{l}</Label>
              <NumberField id={`zf-${k}`} align="left" opts={o} value={cfg[k]} error={errors[k]} onChange={(t) => { setCfg((c) => ({ ...c, [k]: t })); clearErr(k); }} testId={`zf-${k}-input`} /></div>
          ))}
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={!!busy} data-testid="zf-save-button">{busy === "save" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Speichern</Button>
          <Button variant="outline" onClick={run} disabled={!!busy} data-testid="zf-run-button">{busy === "run" && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Jetzt prüfen</Button>
        </div>
      </CardContent>
    </Card>
  );
};
